import { User, Conversation } from '../types';
import { ConversationsService } from './conversationsService';
import { SupabaseService } from './supabaseService';
import { Message } from '../types';

export interface MatchConnectionResult {
  success: boolean;
  conversation?: Conversation;
  isNewConversation?: boolean;
  error?: string;
}

/**
 * Service for handling match connections and conversation creation
 */
export class MatchService {
  
  /**
   * Handles the complete match connection flow
   * 1. Check if conversation exists
   * 2. Create new conversation if needed
   * 3. Return conversation for navigation
   */
  static async handleMatchConnection(
    currentUser: User,
    matchedUser: User
  ): Promise<MatchConnectionResult> {
    try {
      console.log('🤝 Iniciando proceso de conexión de match...', {
        currentUser: currentUser.name,
        matchedUser: matchedUser.name
      });

      // Primero verificar si ya existe una conversación
      console.log('🔍 Verificando conversación existente...');
      const existingResult = await ConversationsService.findConversationBetweenUsers(
        currentUser.id,
        matchedUser.id
      );
      
      let conversationId: string;
      let isNewConversation = false;
      
      if (existingResult.success && existingResult.conversation) {
        console.log('✅ Conversación existente encontrada:', existingResult.conversation.id);
        conversationId = existingResult.conversation.id;
      } else {
        console.log('💬 Creando nueva conversación en Supabase...');
        const createResult = await ConversationsService.createConversation(
          currentUser.id,
          matchedUser.id
        );
        
        if (!createResult.success || !createResult.conversation) {
          console.error('❌ Error al crear conversación:', createResult.error);
          return {
            success: false,
            error: createResult.error || 'No se pudo crear la conversación'
          };
        }
        
        conversationId = createResult.conversation.id;
        isNewConversation = true;
        console.log('✅ Nueva conversación creada:', conversationId);
      }
      
      // Crear objeto de conversación completo para el frontend
      const conversation: Conversation = {
        id: conversationId,
        participants: [currentUser, matchedUser],
        messages: [],
        updatedAt: new Date()
      };
      
      // Enviar mensaje inicial solo si es una conversación nueva
      if (isNewConversation) {
        console.log('💌 Enviando mensaje inicial de match...');
        const initialMessage = this.generateInitialMatchMessage(currentUser, matchedUser);
        
        const messageResult = await ConversationsService.sendMessage(
          conversationId,
          currentUser.id,
          matchedUser.id,
          initialMessage
        );
        
        if (messageResult.success && messageResult.message) {
          conversation.messages = [messageResult.message];
          conversation.lastMessage = messageResult.message;
          console.log('✅ Mensaje inicial enviado');
        } else {
          console.warn('⚠️ No se pudo enviar mensaje inicial:', messageResult.error);
        }
      }
      
      return {
        success: true,
        conversation,
        isNewConversation
      };

    } catch (error) {
      console.error('❌ Error inesperado en conexión de match:', error);
      return {
        success: false,
        error: `Error de conexión: ${error instanceof Error ? error.message : 'Error desconocido'}`
      };
    }
  }

  /**
   * Finds existing conversation between two users
   */
  private static async findExistingConversation(
    user1Id: string,
    user2Id: string
  ): Promise<{ success: boolean; conversation?: Conversation; error?: string }> {
    try {
      console.log('🔍 Buscando conversación existente entre usuarios...');
      
      // Use ConversationsService to find existing conversation
      const result = await ConversationsService.findConversationBetweenUsers(user1Id, user2Id);
      
      if (result.success && result.conversation) {
        console.log('📋 Conversación encontrada:', result.conversation.id);
        
        // Load conversation messages
        const messagesResult = await ConversationsService.getConversationMessages(
          result.conversation.id
        );
        
        if (messagesResult.success && messagesResult.messages) {
          result.conversation.messages = messagesResult.messages;
        }
      }
      
      return result;
      
    } catch (error) {
      console.error('❌ Error al buscar conversación existente:', error);
      return {
        success: false,
        error: 'Error al buscar conversación existente'
      };
    }
  }

  /**
   * Creates a new conversation for matched users
   */
  private static async createMatchConversation(
    currentUser: User,
    matchedUser: User
  ): Promise<{ success: boolean; conversation?: Conversation; error?: string }> {
    try {
      console.log('💬 Creando conversación de match...');
      
      // Create conversation using ConversationsService
      const result = await ConversationsService.createConversation(
        currentUser.id,
        matchedUser.id
      );

      if (!result.success) {
        return result;
      }

      // Enhance conversation with user data
      if (result.conversation) {
        result.conversation.participants = [currentUser, matchedUser];
        result.conversation.messages = [];
        result.conversation.updatedAt = new Date();
      }

      return result;
      
    } catch (error) {
      console.error('❌ Error al crear conversación de match:', error);
      return {
        success: false,
        error: 'Error al crear conversación'
      };
    }
  }

  /**
   * Sends initial match message to start the conversation
   */
  private static async sendInitialMatchMessage(
    conversationId: string,
    currentUser: User,
    matchedUser: User
  ): Promise<void> {
    try {
      console.log('💌 Enviando mensaje inicial de match...');
      
      const initialMessage = this.generateInitialMatchMessage(currentUser, matchedUser);
      
      const result = await ConversationsService.sendMessage(
        conversationId,
        currentUser.id,
        matchedUser.id,
        initialMessage
      );

      if (result.success) {
        console.log('✅ Mensaje inicial enviado exitosamente');
      } else {
        console.warn('⚠️ No se pudo enviar mensaje inicial:', result.error);
      }
      
    } catch (error) {
      console.warn('⚠️ Error al enviar mensaje inicial:', error);
      // No throw error - this is not critical for the flow
    }
  }

  /**
   * Generates personalized initial match message
   */
  private static generateInitialMatchMessage(
    currentUser: User,
    matchedUser: User
  ): string {
    const currentUserType = currentUser.userType;
    const matchedUserType = matchedUser.userType;

    if (currentUserType === 'entrepreneur' && matchedUserType === 'investor') {
      const entrepreneur = currentUser as any;
      return `¡Hola ${matchedUser.name}! Me encantaría conectar contigo. Estoy trabajando en ${entrepreneur.company} en el sector ${entrepreneur.industry}. ¿Te gustaría conocer más sobre nuestro proyecto?`;
    }
    
    if (currentUserType === 'investor' && matchedUserType === 'entrepreneur') {
      const entrepreneur = matchedUser as any;
      return `¡Hola ${matchedUser.name}! Me interesa mucho tu proyecto en ${entrepreneur.industry}. Como inversor, me gustaría conocer más sobre ${entrepreneur.company} y cómo puedo ayudarte.`;
    }

    // Fallback for same user types or other cases
    return `¡Hola ${matchedUser.name}! Me encantaría conectar contigo y explorar oportunidades de colaboración. ¿Te parece bien que conversemos?`;
  }

  /**
   * Records match analytics for future improvements
   */
  static async recordMatchAnalytics(
    currentUser: User,
    matchedUser: User,
    matchType: 'normal' | 'super'
  ): Promise<void> {
    try {
      console.log('📊 Registrando analytics de match...', {
        currentUserType: currentUser.userType,
        matchedUserType: matchedUser.userType,
        matchType
      });

      // Here you would save match analytics to Supabase
      // For now, just log the event
      const analyticsData = {
        timestamp: new Date().toISOString(),
        user1_id: currentUser.id,
        user1_type: currentUser.userType,
        user2_id: matchedUser.id,
        user2_type: matchedUser.userType,
        match_type: matchType,
        industries: this.extractIndustries(currentUser, matchedUser)
      };

      console.log('📈 Analytics data:', analyticsData);
      
    } catch (error) {
      console.warn('⚠️ Error al registrar analytics:', error);
      // Don't throw - analytics failure shouldn't break the flow
    }
  }

  /**
   * Extracts industries from users for analytics
   */
  private static extractIndustries(user1: User, user2: User): string[] {
    const industries: string[] = [];
    
    if (user1.userType === 'entrepreneur') {
      industries.push((user1 as any).industry);
    }
    if (user1.userType === 'investor') {
      industries.push(...(user1 as any).focusIndustries || []);
    }
    
    if (user2.userType === 'entrepreneur') {
      industries.push((user2 as any).industry);
    }
    if (user2.userType === 'investor') {
      industries.push(...(user2 as any).focusIndustries || []);
    }
    
    return [...new Set(industries)]; // Remove duplicates
  }
}